import { useQueries, useQuery } from '@tanstack/react-query'
import { Button, Space, Table, Tag, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { getInvoiceRefundView, getPaymentOrderRefundView, refundQueries, workspaceQueries } from '../../../../Actions'
import CreditPackRefundModal from './Refunds/CreditPackRefundModal'
import RecordRefundModal from './Refunds/RecordRefundModal'
import RefundQuoteModal from './Refunds/RefundQuoteModal'
import RollbackIntentModal from './Refunds/RollbackIntentModal'
import {
  BILLING_INTERVAL_LABEL,
  BillingHistoryItem,
  Invoice,
  InvoiceRefundView,
  RefundTarget,
  RefundTargetView,
  refundTargetKey,
  refundTargetOf,
  resolveBillingInterval,
  resolveInvoiceRefundStatus,
  toInvoice,
} from '../../../../types/refund'

type Props = {
  workspaceId: number
  /** 대장의 해당 행으로 이동한다. 두 섹션을 아는 것은 부모(`SubscriptionPayment`)뿐이다 */
  onShowRefundRow?: (refundId: number) => void
}

const isPaid = (item: BillingHistoryItem) => String(item.status ?? '').toUpperCase() === 'PAID'

const isCreditPack = (item: BillingHistoryItem) => item.type === 'CREDIT_PACK'

/** 행 key. 🔴 종류를 붙인다 — 청구서 23 과 결제 건 23 이 한 목록에 함께 있을 수 있다 */
const rowKeyOf = (item: BillingHistoryItem) => {
  const target = refundTargetOf(item)
  return target ? refundTargetKey(target) : `${item.type}-${item.paidAt}-${item.orderNumber}`
}

/** 환불 현황 쿼리. 대상 종류마다 API 가 다르다 — 결제 건 id 로 청구서 API 를 부르면 남의 현황을 읽는다 */
const refundViewQuery = (target: RefundTarget) =>
  target.kind === 'INVOICE'
    ? {
        queryKey: refundQueries.invoiceView(target.invoiceId).queryKey,
        queryFn: (): Promise<RefundTargetView> => getInvoiceRefundView(target.invoiceId),
      }
    : {
        queryKey: refundQueries.paymentOrderView(target.paymentOrderId).queryKey,
        queryFn: (): Promise<RefundTargetView> => getPaymentOrderRefundView(target.paymentOrderId),
      }

const money = (value?: number | null) => (value == null ? '-' : `${Number(value).toLocaleString()}원`)
const credit = (value?: number | null) => (value == null ? '-' : `${Number(value).toLocaleString()}크레딧`)

/**
 * 소멸일이 지난 크레딧 팩 결제 건인가 (환불대장 v4 P1 "소멸된 행" · 정책 8절).
 *
 * 소멸일은 **당일까지** 유효하다 — 다음 날부터 소멸이다. 날짜 단위로 오늘(담당자 브라우저, KST)과 비교한다.
 * 서버 산정도 같은 규칙으로 끊는다(기준일이 소멸일 뒤면 400) — 버튼에서 미리 막을 뿐이다.
 */
const isExpiredPack = (item: BillingHistoryItem) =>
  isCreditPack(item) && !!item.expiresOn && item.expiresOn < dayjs().format('YYYY-MM-DD')

const EXPIRED_PACK_HINT = '소멸됨 · 재량 처리는 기록 추가로'

const OPEN_REFUND_HINT =
  '이 결제 건에 아직 처리되지 않은 환불 기록이 있습니다. 기록 보기로 확인하고, 다시 산정해야 하면 그 행을 철회한 뒤 접수하세요'

/**
 * 결제 건 목록 (화면 명세 1절 · aipark-front #266 · 환불대장 Phase 2 P1 #280)
 *
 * 각 행이 환불의 출발점이다 — 산정 미리보기·되감기·기록 추가가 전부 여기서 시작한다.
 *
 * 구독 결제(청구서)와 크레딧 팩 구매(단건 상점 결제 건)가 **결제일 순 한 목록**으로 온다
 * (`billing-history`, aivatar #533). 크레딧 팩 결제는 청구서를 만들지 않아(D-121) 종전
 * `/invoices` 에는 없었다. 행 종류(`type`)가 환불 현황·접수 API 를 가른다 — `refundTargetOf`.
 */
export default function Invoices({ workspaceId, onShowRefundRow }: Props) {
  const [page, setPage] = useState(0)
  /** 각 모달의 대상. null 이면 닫혀 있다 */
  const [quoteTarget, setQuoteTarget] = useState<Invoice | null>(null)
  const [rollbackTarget, setRollbackTarget] = useState<Invoice | null>(null)
  const [recordTarget, setRecordTarget] = useState<RefundTarget | null>(null)
  const [creditPackTarget, setCreditPackTarget] = useState<BillingHistoryItem | null>(null)

  const { isLoading, data } = useQuery({
    ...workspaceQueries.detail(workspaceId)._ctx.billingHistory({ page }),
  })

  const items: BillingHistoryItem[] = data?.content ?? []
  // 결제 실패 행은 환불 대상이 아니라 조회를 건다 (서버도 PAID 만 환불을 받는다)
  const refundTargets = items
    .filter(isPaid)
    .map(refundTargetOf)
    .filter((target): target is RefundTarget => target != null)

  const refundQueryResults = useQueries({
    queries: refundTargets.map((target) => ({ ...refundViewQuery(target) })),
  })

  /** 현황은 대상 key(`INVOICE-23`·`PAYMENT_ORDER-23`)로 든다. 숫자 id 로 들면 둘이 덮어쓴다 */
  const refundViewMap = refundTargets.reduce<Record<string, RefundTargetView | undefined>>(
    (acc, target, index) => ({ ...acc, [refundTargetKey(target)]: refundQueryResults[index]?.data }),
    {},
  )

  /** 아직 응답이 없는 행. 되감기 가부를 "모른다" 와 "거짓" 을 구분하는 데 쓴다 */
  const refundViewPendingMap = refundTargets.reduce<Record<string, boolean>>(
    (acc, target, index) => ({ ...acc, [refundTargetKey(target)]: refundQueryResults[index]?.isPending ?? true }),
    {},
  )

  const viewOf = (item: BillingHistoryItem) => {
    const target = refundTargetOf(item)
    return target ? refundViewMap[refundTargetKey(target)] : undefined
  }

  /** 청구서 현황만 되감기 칸을 가진다. 아래 되감기 판정은 구독 행에서만 부른다 */
  const invoiceKeyOf = (invoice: Invoice) => refundTargetKey({ kind: 'INVOICE', invoiceId: invoice.id })
  const invoiceViewOf = (invoice: Invoice) => refundViewMap[invoiceKeyOf(invoice)] as InvoiceRefundView | undefined

  /**
   * 되감기 버튼을 렌더할지 (명세 7-3 · aipark-front #269).
   *
   * 판정은 서버가 한다 — `rollbackAvailable` 하나만 읽는다. 자격 6조건을 프론트에 복제하지
   * 않는다는 #266 합의는 그대로고, 바뀐 것은 서버가 결과를 직접 내려준다는 점이다.
   *
   * 두 갈래의 `undefined` 를 구분한다.
   * - **응답 전**: 렌더하지 않는다. `!== false` 폴백으로 먼저 띄우면 버튼이 보였다 사라지고,
   *   그 틈에 눌리면 대상이 아닌 건의 모달이 열린다
   * - **응답에 필드가 없음**(BE aivatar #516 배포 전): 종전대로 노출한다. 배포 순서에 묶이지
   *   않으려는 폴백이고, 그 경로에서는 모달의 산정 400 이 가부를 답한다
   */
  const canRollback = (invoice: Invoice) => {
    if (refundViewPendingMap[invoiceKeyOf(invoice)] ?? true) return false
    return invoiceViewOf(invoice)?.rollbackAvailable !== false
  }

  /**
   * 되감기 API 에 넘길 전환 약정 id. **결제 건에서 유도하지 않고 서버가 지목한 값을 쓴다.**
   * 필드가 없는 동안(BE 배포 전)만 종전처럼 결제 건의 구독으로 떨어진다.
   */
  const rollbackSubscriptionId = (invoice: Invoice) =>
    invoiceViewOf(invoice)?.rollbackSubscriptionId ?? invoice.subscription?.subscriptionId ?? null

  /**
   * 아직 끝나지 않은 환불이 걸려 있는 결제 건인가 (화면 명세 1절 · 화면 구성 v3 S1).
   *
   * 접수·보류 행이 있으면 `환불` 버튼을 비활성으로 두고 그 기록으로 안내한다 — 같은 결제 건을
   * 두 번 접수하면 산정값이 다른 행이 둘 생기고, 어느 쪽 금액을 결제대행사에 넣어야 하는지가
   * 화면에서 사라진다. 정정은 원 행을 철회한 뒤 다시 접수하는 경로다.
   *
   * `unsettledTotal` 은 접수·보류 합계다. 처리 완료·철회 행은 여기 들어가지 않으므로, 끝난
   * 결제 건은 다시 접수할 수 있다(부분 환불 뒤의 추가 환불).
   */
  const hasOpenRefund = (item: BillingHistoryItem) => (viewOf(item)?.unsettledTotal ?? 0) > 0

  /** 처리 완료된 환불이 있는 크레딧 팩 결제 건인가. 현황을 아직 못 읽었으면 거짓(종전대로 [환불]) */
  const isRefundedPack = (item: BillingHistoryItem) => isCreditPack(item) && (viewOf(item)?.refundedTotal ?? 0) > 0

  /** 대장에서 가리킬 행. 가장 최근 환불 기록을 지목한다 */
  const latestRefundId = (item: BillingHistoryItem) => {
    const refunds = viewOf(item)?.refunds ?? []
    return refunds.at(-1)?.id ?? null
  }

  /** 구독 행의 동작. 종전 청구서 행 그대로다 — 모달들이 `Invoice` 를 받는다 */
  const renderSubscriptionActions = (item: BillingHistoryItem, r: Invoice) => (
    <>
      <Tooltip
        title={
          hasOpenRefund(item)
            ? OPEN_REFUND_HINT
            : r.subscription?.subscriptionId
              ? undefined
              : '이 결제 건에 구독 링크가 없어 산정 대상을 지목할 수 없습니다'
        }
      >
        <Button
          size="small"
          type="primary"
          disabled={!r.subscription?.subscriptionId || hasOpenRefund(item)}
          onClick={() => setQuoteTarget(r)}
        >
          환불
        </Button>
      </Tooltip>
      {/* 🔴 되감기 자격을 화면이 판정하지 않는다 — 서버가 낸 rollbackAvailable 을
          읽어 거짓이면 **비노출**이다(명세 7-3, 비활성이 아니다). 사유
          (rollbackReason)는 띄우지 않는다 — 버튼이 없어 붙일 자리가 없다.
          아래 disabled 는 BE(aivatar #516) 배포 전 폴백 경로에서만 닿는다 */}
      {canRollback(r) ? (
        <Tooltip
          title={
            rollbackSubscriptionId(r)
              ? '전환 결제를 무릅니다. 잔류·이탈을 고른 뒤 접수합니다'
              : '이 결제 건에 구독 링크가 없어 되감기 대상을 지목할 수 없습니다'
          }
        >
          <Button size="small" disabled={!rollbackSubscriptionId(r)} onClick={() => setRollbackTarget(r)}>
            되감기
          </Button>
        </Tooltip>
      ) : null}
      <Tooltip title="산정 API 가 없는 환불(회사 귀책 등)을 이미 실행한 결과로 남깁니다">
        <Button size="small" onClick={() => setRecordTarget({ kind: 'INVOICE', invoiceId: r.id })}>
          기록 추가
        </Button>
      </Tooltip>
    </>
  )

  /**
   * 크레딧 팩 행의 동작. 되감기는 없다 — 되감을 약정이 없다. 접수·보류 중인 환불이 걸려 있으면
   * 구독 행과 같은 이유로 `환불` 을 막는다(한 결제 건에 산정값이 다른 접수 행이 둘 생긴다).
   */
  const renderCreditPackActions = (item: BillingHistoryItem, paymentOrderId: number) => (
    <>
      {/*
        환불이 끝난 행(환불 완료 · 부분 환불)은 [환불] 대신 [기록 보기]만 남는다(환불대장 v4 P1). 처리 완료 확정이
        그 결제 건의 남은 크레딧을 모두 없애므로(D-124) 부분 환불이었어도 다시 환불할 크레딧이 없다.
        구독 행의 "부분 환불 뒤 추가 환불"과 다르다
      */}
      {isRefundedPack(item) ? null : isExpiredPack(item) ? (
        <Space size={4}>
          <Button size="small" type="primary" disabled>
            환불
          </Button>
          <Typography.Text type="secondary">{EXPIRED_PACK_HINT}</Typography.Text>
        </Space>
      ) : (
        <Tooltip title={hasOpenRefund(item) ? OPEN_REFUND_HINT : undefined}>
          <Button size="small" type="primary" disabled={hasOpenRefund(item)} onClick={() => setCreditPackTarget(item)}>
            환불
          </Button>
        </Tooltip>
      )}
      {/* 소멸한 건은 산정이 400 으로 끊는다 — 재량 처리는 이미 실행한 결과로 여기서 남긴다 */}
      <Tooltip title="산정할 수 없는 환불(소멸 후 재량 처리·회사 귀책 등)을 이미 실행한 결과로 남깁니다">
        <Button size="small" onClick={() => setRecordTarget({ kind: 'PAYMENT_ORDER', paymentOrderId })}>
          기록 추가
        </Button>
      </Tooltip>
    </>
  )

  return (
    <div style={{ marginBottom: 20 }}>
      <Typography.Title level={4}>결제 건 목록</Typography.Title>
      <Table
        dataSource={items}
        rowKey={rowKeyOf}
        loading={isLoading}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{
          total: data?.totalElements,
          current: page + 1,
          onChange: (newPage) => setPage(newPage - 1),
          position: ['bottomCenter'],
        }}
        columns={[
          {
            title: '결제일',
            key: 'paidAt',
            // 결제 시각이 없는 실패 청구서는 서버가 생성 시각을 싣는다 — 결제 시도 날짜다
            render: (t, r: BillingHistoryItem) => (r.paidAt ? dayjs(r.paidAt).format('YYYY-MM-DD HH:mm') : '-'),
          },
          {
            title: '구분',
            key: 'interval',
            // 명세의 "전환 결제" 는 여기서 판정하지 않는다 — 서버 자격 규칙이 6조건이라
            // 프론트가 복제하면 두 곳이 갈린다(aivatar #488 ②). 되감기 버튼의 노출은 서버가
            // 내려주는 rollbackAvailable 로 정하지만(#269), 이 라벨은 여전히 판정하지 않는다.
            render: (t, r: BillingHistoryItem) =>
              isCreditPack(r) ? (
                // 단건 상점 결제다. 결제대행사 화면에서 정기결제 상점이 아니라 단건 상점을 찾아야 한다
                <Space direction="vertical" size={0}>
                  <Tag color="geekblue">크레딧 팩</Tag>
                  <Typography.Text type="secondary">단건 상점</Typography.Text>
                </Space>
              ) : (
                BILLING_INTERVAL_LABEL[resolveBillingInterval(r.subscription?.intervalMonth)]
              ),
          },
          {
            title: '항목',
            key: 'item',
            render: (t, r: BillingHistoryItem) =>
              isCreditPack(r) ? (
                <Space direction="vertical" size={0}>
                  <span>{r.itemName ?? '-'}</span>
                  {/* 남은 크레딧 = 그 결제 건의 미사용분(명세 7-2 역산식, 환불대장 v4 P1). 모르면 붙이지 않는다 */}
                  <Typography.Text type="secondary">
                    지급 {credit(r.grantedCredit)} · 소멸일 {r.expiresOn ?? '-'}
                    {r.remainingCredit != null ? ` · 남은 ${Number(r.remainingCredit).toLocaleString()}` : ''}
                  </Typography.Text>
                </Space>
              ) : (
                (r.itemName ?? r.subscription?.plan?.name ?? '-')
              ),
          },
          {
            title: '청구액',
            key: 'price',
            render: (t, r: BillingHistoryItem) => (
              <Space direction="vertical" size={0}>
                <span>{money(r.price)}</span>
                <Typography.Text type="secondary">부가세 포함</Typography.Text>
              </Space>
            ),
          },
          {
            title: '결제 고유번호',
            key: 'paymentNumber',
            render: (t, r: BillingHistoryItem) =>
              r.paymentNumber ? <Typography.Text copyable>{r.paymentNumber}</Typography.Text> : '-',
          },
          {
            title: '주문번호',
            key: 'orderNumber',
            render: (t, r: BillingHistoryItem) =>
              r.orderNumber ? <Typography.Text copyable>{r.orderNumber}</Typography.Text> : '-',
          },
          {
            title: '환불 상태',
            key: 'refundStatus',
            // InvoiceRefundState 만으로는 "접수됨" 이 안 보인다 — 그 집계는 처리 완료 행만
            // 세기 때문에 접수만 걸린 청구서도 NONE 으로 온다. unsettledTotal 을 함께 본다.
            render: (t, r: BillingHistoryItem) => {
              if (!isPaid(r)) return <Tag color="red">결제 실패</Tag>
              const { label, color } = resolveInvoiceRefundStatus(viewOf(r))
              return <Tag color={color}>{label}</Tag>
            },
          },
          {
            title: '동작',
            key: 'actions',
            render: (t, r: BillingHistoryItem) => {
              const refundId = latestRefundId(r)
              const invoice = toInvoice(r)
              return (
                <Space size={4}>
                  {isPaid(r) && invoice ? renderSubscriptionActions(r, invoice) : null}
                  {isPaid(r) && isCreditPack(r) && r.paymentOrderId != null ? renderCreditPackActions(r, r.paymentOrderId) : null}
                  {refundId && onShowRefundRow ? (
                    <Button size="small" onClick={() => onShowRefundRow(refundId)}>
                      기록 보기
                    </Button>
                  ) : null}
                  {/* 크레딧 팩은 결제대행사(단건 상점) 영수증이다. 아직 못 받은 건은 비어 있다(aivatar #538) */}
                  {r.slipUrl ? (
                    <Button size="small" type="link" href={r.slipUrl} target="_blank" rel="noreferrer">
                      영수증
                    </Button>
                  ) : null}
                </Space>
              )
            },
          },
        ]}
      />

      <RefundQuoteModal
        open={!!quoteTarget}
        workspaceId={workspaceId}
        invoice={quoteTarget}
        onClose={() => setQuoteTarget(null)}
      />
      <RollbackIntentModal
        open={!!rollbackTarget}
        workspaceId={workspaceId}
        invoice={rollbackTarget}
        subscriptionId={rollbackTarget ? rollbackSubscriptionId(rollbackTarget) : null}
        onClose={() => setRollbackTarget(null)}
      />
      <CreditPackRefundModal
        open={!!creditPackTarget}
        workspaceId={workspaceId}
        item={creditPackTarget}
        onClose={() => setCreditPackTarget(null)}
      />
      <RecordRefundModal
        open={!!recordTarget}
        workspaceId={workspaceId}
        target={recordTarget}
        onClose={() => setRecordTarget(null)}
      />
    </div>
  )
}
